import { Box } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import ImageCropper from "../../../ImageCropper";
import Input from "../../../Input";
import TextView from "../../../TextView";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { getVideoUrl } from "../../CodeBlocks/Video";

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${colors.lightBorder};
  margin-bottom: 20px;
`;

const Icon = styled.img`
  cursor: pointer;
  margin-left: 10px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${(props) => (props.alignTop ? "flex-start" : "center")};
`;

const MediaPreview = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  margin-right: 16px;
  overflow: hidden;
  object-fit: cover;
  border: 1px solid ${colors.lightGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.lightGrey};
  flex-shrink: 0; // Prevent thumbnail from shrinking
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 4px;
`;

const FilenameContainer = styled.div`
  width: 100%;
  padding-right: ${(props) => (props.hasIcons ? "10px" : "0")};
`;

const Filename = styled(TextView)`
  text-align: left;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  line-height: 1.4;
  max-width: 100%;
`;

const URLContainer = styled.div`
  margin-top: 16px;
  padding: 6px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid ${colors.lightBorder};
`;

const URLRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const URLInput = styled.input`
  flex: 1;
  padding: 0px;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;
  color: ${colors.darkGrey};
  border: none;
  outline: none;

  &:focus {
    border-color: none;
  }
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  background-color: #9b5af8;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.darkPurple};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const UploadedMedia = ({
  type = "image",
  media,
  title,
  onChange,
  showTitle,
  handleCheckbox,
  handleEditMedia,
  originalImage,
  allowEdit = true,
  lockedAspectRatio = null,
  persistCrop = false,
  isTitleCustomizable = allowEdit,
  handleMediaRemove = () => {},
  pathType,
  showURL = true,
}) => {
  const [open, setOpen] = useState(false);

  const handleCopyURL = async () => {
    try {
      await navigator.clipboard.writeText(media);
      // You could add a toast notification here if needed
      toast.success("URL copied to clipboard");
    } catch (err) {
      console.error("Failed to copy URL:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = media;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const renderMedia = () => {
    switch (type) {
      case "image":
        return (
          <img
            src={media}
            alt="uploaded media"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        );
      case "video":
        const videoUrl = getVideoUrl(media);
        const isLoomVideo = videoUrl ? videoUrl?.includes("loom.com/embed/") : false;

        return isLoomVideo ? (
          <iframe
            src={videoUrl}
            frameBorder="0"
            allowFullScreen
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <ReactPlayer
            url={media}
            width={"100%"}
            height={"100%"}
            playing={false}
          />
        );
      case "document":
        return <Iframe src={media} title="uploaded document" />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Row>
        <TextView variant="subtitle">
          Uploaded {type.charAt(0).toUpperCase() + type.slice(1)}
        </TextView>
        {allowEdit && (
          <Box>
            {type === "image" && (
              <Icon
                width={20}
                height={20}
                src={ASSETS.EditImage}
                onClick={() => setOpen(true)}
              />
            )}
            <Icon
              width={20}
              height={20}
              src={ASSETS.deleteIcon2}
              onClick={handleMediaRemove}
            />
          </Box>
        )}
      </Row>
      <Row style={{ marginTop: 10 }} alignTop={true}>
        <MediaPreview>{renderMedia()}</MediaPreview>
        {allowEdit && isTitleCustomizable ? (
          <div style={{ width: "100%" }}>
            <Row>
              <Input
                containerStyle={{ width: "100%", marginBottom: 0 }}
                variant={"secondary"}
                value={title}
                onChange={(e) => onChange(e.target.value)}
                maxLength={40}
                placeholder={type === "image" ? "Add image name" : "Add title"}
              />
              <TextView style={{ width: 55, textAlign: "right" }}>
                {title?.length}/40
              </TextView>
            </Row>

            <TextView>
              <Checkbox
                onChange={handleCheckbox}
                checked={showTitle}
                sx={{
                  color: colors.lightPurple,
                  "&.Mui-checked": {
                    color: colors.purple,
                  },
                }}
              />
              Display Caption
            </TextView>
          </div>
        ) : (
          <FilenameContainer hasIcons={allowEdit}>
            <Filename>{title}</Filename>
          </FilenameContainer>
        )}
      </Row>
      {showURL && (
        <URLContainer>
          <URLRow>
            <URLInput
              type="text"
              value={media}
              readOnly
              placeholder="Media URL"
            />
            <CopyButton onClick={handleCopyURL}>Copy URL</CopyButton>
          </URLRow>
        </URLContainer>
      )}
      {type === "image" && (
        <ImageCropper
          persistCrop={persistCrop}
          lockedAspectRatio={lockedAspectRatio}
          src={originalImage}
          open={open}
          handleClose={() => setOpen(false)}
          handleEditImage={(val) => handleEditMedia({ src: val })}
          pathType={pathType}
        />
      )}
    </Container>
  );
};

export default UploadedMedia;
